import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X, AlertTriangle, RefreshCw, Send } from 'lucide-react';
import QRCode from 'react-qr-code';
import { shareSession, type ShareResponse, ApiError } from '@lib/api';
import { useT, type StringKey } from '@lib/i18n';
import { useSessionStore } from '@stores/sessionStore';
import type {
  Language,
  Session,
  Unit,
} from '@lib/types';
import type { PriceBreakdown, YieldBreakdown } from '@lib/pricing';

type ShareState = 'loading' | 'success' | 'error';

interface ShareModalProps {
  isOpen: boolean;
  onClose: () => void;
  session: Session;
  unit: Unit;
  breakdown: PriceBreakdown;
  yieldData: YieldBreakdown;
  language: Language;
}

export default function ShareModal({
  isOpen,
  onClose,
  session,
  unit,
  breakdown,
  yieldData,
  language,
}: ShareModalProps) {
  const t = useT();
  const navigate = useNavigate();
  const resetSession = useSessionStore((s) => s.resetSession);

  const [state, setState] = useState<ShareState>('loading');
  const [response, setResponse] = useState<ShareResponse | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  // Track the latest in-flight call so a user reopening the modal doesn't
  // commit a stale prior result.
  const callIdRef = useRef(0);

  // Lock background scroll while modal is open.
  useEffect(() => {
    if (!isOpen) return;
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      document.body.style.overflow = prev;
    };
  }, [isOpen]);

  // Close on Escape.
  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const sendReport = () => {
    const id = ++callIdRef.current;
    setState('loading');
    setErrorMsg(null);
    setResponse(null);
    shareSession({ session, unit, breakdown, yieldData, language })
      .then((res) => {
        if (id !== callIdRef.current) return;
        setResponse(res);
        setState('success');
      })
      .catch((err: unknown) => {
        if (id !== callIdRef.current) return;
        const msg =
          err instanceof ApiError
            ? `${err.code}: ${err.message}`
            : err instanceof Error
              ? err.message
              : String(err);
        setErrorMsg(msg);
        setState('error');
      });
  };

  // Kick the API call once each time the modal opens.
  useEffect(() => {
    if (!isOpen) return;
    sendReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="share-modal-title"
      className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-navy-900/60 backdrop-blur-sm animate-fade-in"
      onClick={(e) => {
        // click on overlay closes
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className="card w-full max-w-md p-6 md:p-8 relative animate-slide-up">
        <button
          type="button"
          aria-label={t('share.close')}
          onClick={onClose}
          className="absolute top-3 end-3 p-1.5 rounded-full text-navy-400 hover:bg-navy-50 hover:text-navy"
        >
          <X className="w-5 h-5" />
        </button>

        <h2
          id="share-modal-title"
          className="text-xl md:text-2xl font-bold text-navy mb-4"
        >
          {t('share.title')}
        </h2>

        {state === 'loading' && (
          <LoadingView label={t('share.loading')} />
        )}

        {state === 'success' && response && (
          <SuccessView
            response={response}
            phone={session.clientPhone}
            t={t}
            onClose={onClose}
            onNewSession={() => {
              resetSession();
              onClose();
              navigate('/welcome');
            }}
          />
        )}

        {state === 'error' && (
          <ErrorView
            message={errorMsg}
            t={t}
            onRetry={sendReport}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

function LoadingView({ label }: { label: string }) {
  return (
    <div className="py-10 flex flex-col items-center gap-4 text-navy-700">
      <Loader2 className="w-10 h-10 animate-spin text-gold" />
      <p className="text-sm md:text-base text-center">{label}</p>
    </div>
  );
}

function SuccessView({
  response,
  phone,
  t,
  onClose,
  onNewSession,
}: {
  response: ShareResponse;
  phone: string;
  t: (k: StringKey) => string;
  onClose: () => void;
  onNewSession: () => void;
}) {
  const sentLine = t('share.sentTo').replace('{phone}', phone || '—');
  // Phase 8f-bcs B.3 — surface real WhatsApp send result
  const wa = response.whatsapp;
  return (
    <div className="flex flex-col items-center gap-4">
      <div className="inline-flex items-center justify-center gap-2 text-available text-sm font-semibold">
        <Send className="w-4 h-4" />
        {t('share.successTitle')}
      </div>

      {wa.status === 'sent' && (
        <p className="text-sm md:text-base text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-center leading-relaxed max-w-md">
          {t('share.whatsappSent')}{' '}
          <span className="num-en font-semibold">{phone || '—'}</span>
        </p>
      )}
      {wa.status === 'failed' && (
        <p className="text-sm text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2 text-center leading-relaxed max-w-md">
          {t('share.whatsappFailed')}
          <br />
          <span className="text-xs">{t('share.whatsappFallback')}</span>
        </p>
      )}
      {wa.status === 'mocked' && (
        <p className="text-sm md:text-base text-navy-700 text-center leading-relaxed">
          {sentLine}
        </p>
      )}

      <div className="bg-white p-3 rounded-lg border border-navy-100 inline-block">
        <QRCode
          value={response.sessionUrl}
          size={220}
          fgColor="#0A1628"
          bgColor="#FFFFFF"
        />
      </div>

      <p className="text-xs text-navy-500 text-center">{t('share.scanQr')}</p>

      <p className="text-[11px] text-navy-400 num-en break-all text-center">
        {response.sessionUrl}
      </p>

      {/* Phase 8f-bcs B.2 — server-rendered PDF download */}
      {response.pdfUrl && (
        <button
          type="button"
          onClick={() => window.open(response.pdfUrl!, '_blank', 'noopener,noreferrer')}
          className="bg-gold hover:bg-gold-700 text-navy font-semibold rounded-lg px-4 py-2 text-sm transition-colors"
        >
          {t('share.downloadPdf')}
        </button>
      )}
      {!response.pdfUrl && response.pdfError && (
        <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2 text-center max-w-sm">
          {t('share.pdfFailed')}
        </p>
      )}

      {/* B.4 (2026-05-08) — assignee feedback. Only shown when the
          template send succeeded; shape parallels whatsappSent so the
          UI reads cleanly: "Sent to +968…" + "Lead assigned to منذر". */}
      {wa.status === 'sent' && response.advisor && wa.assigneeSet && (
        <p className="text-[12px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded px-3 py-2 text-center max-w-md">
          ✅ {t('share.advisorAssigned').replace('{advisor}', response.advisor.name)}
        </p>
      )}
      {wa.status === 'sent' && response.advisor && !wa.assigneeSet && (
        <p className="text-[12px] text-amber-800 bg-amber-50 border border-amber-300 rounded px-3 py-2 text-center max-w-md">
          ⚠ {t('share.advisorAssignFailed')}
        </p>
      )}

      {response.backend === 'memory' && (
        <p className="text-[11px] text-gold-700 bg-gold-50 border border-gold/30 rounded px-2 py-1 text-center">
          {t('share.devBackend')}
        </p>
      )}

      <div className="flex flex-wrap gap-2 justify-center w-full pt-2">
        <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-5 text-sm">
          {t('share.close')}
        </button>
        <button type="button" onClick={onNewSession} className="btn-primary !py-2 !px-5 text-sm">
          {t('share.newSession')}
        </button>
      </div>
    </div>
  );
}

function ErrorView({
  message,
  t,
  onRetry,
  onClose,
}: {
  message: string | null;
  t: (k: StringKey) => string;
  onRetry: () => void;
  onClose: () => void;
}) {
  return (
    <div className="flex flex-col items-center gap-4 py-2">
      <div className="w-12 h-12 rounded-full bg-red-50 text-sold flex items-center justify-center">
        <AlertTriangle className="w-6 h-6" />
      </div>
      <h3 className="text-base font-bold text-sold">{t('share.errorTitle')}</h3>
      <p className="text-sm text-navy-600 text-center leading-relaxed">
        {t('share.errorBody')}
      </p>
      {message && (
        <p className="text-[11px] text-navy-400 num-en break-all text-center px-2">
          {message}
        </p>
      )}
      <div className="flex flex-wrap gap-2 justify-center w-full pt-1">
        <button type="button" onClick={onClose} className="btn-ghost !py-2 !px-5 text-sm">
          {t('share.close')}
        </button>
        <button type="button" onClick={onRetry} className="btn-primary !py-2 !px-5 text-sm">
          <RefreshCw className="w-4 h-4" />
          {t('share.retry')}
        </button>
      </div>
    </div>
  );
}
